import * as React from 'react';
import { ChevronRight, Layers } from 'lucide-react';
import type { Space } from '@klareospace/pharos-lib-types';
import { cn } from '@klareospace/pharos-lib-web-utils';
import { useTranslation } from 'react-i18next';
import { useMasterContext } from '&/master';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SpaceRowProps {
  canSelectSpace: boolean;
  onBrowseSpace: (spaceId: string, spaceName: string) => void;
  onClose: () => void;
  siteCount: number;
  space: Pick<Space, 'id' | 'name'>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SpaceRow({
  canSelectSpace,
  onBrowseSpace,
  onClose,
  siteCount,
  space,
}: SpaceRowProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const { selectSpace, spaceId: currentSpaceId } = useMasterContext();
  const isCurrentSpace = space.id === currentSpaceId;
  const siteLabel = siteCount === 1 ? t('mainSelector.site') : t('mainSelector.sitesCount');

  const handleRowClick = (): void => {
    if (canSelectSpace) {
      selectSpace(space.id);
    }
    onBrowseSpace(space.id, space.name);
  };

  const handleSelectClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    selectSpace(space.id);
    onClose();
  };

  const handleChevronClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onBrowseSpace(space.id, space.name);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2.5 py-2 rounded-md transition-colors cursor-pointer group/space-row',
        isCurrentSpace ? 'bg-accent' : 'hover:bg-muted',
      )}
      onClick={handleRowClick}
    >
      <Layers className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <span className="flex-1 text-xs truncate">{space.name}</span>

      {/* Site count / Select toggle */}
      <button className="text-[11px] shrink-0" onClick={canSelectSpace ? handleSelectClick : undefined} type="button">
        <span className={cn('text-muted-foreground', canSelectSpace && 'group-hover/space-row:hidden')}>
          {siteCount} {siteLabel}
        </span>
        {canSelectSpace && (
          <span className="text-primary font-medium hidden group-hover/space-row:inline">
            {t('mainSelector.select')}
          </span>
        )}
      </button>

      {/* Browse chevron — always visible */}
      <button
        aria-label={`Browse sites in ${space.name}`}
        className="p-0.5 rounded hover:bg-accent transition-colors shrink-0"
        onClick={handleChevronClick}
        type="button"
      >
        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
    </div>
  );
}
